/**
 * Academic Neobrutalism Universal Button Loading & Process Synchronization Service
 * 
 * Provides tactile, instantaneous (0ms) loading feedback and process locking for:
 * 1. Immediate visual response on pointerdown & click without lag/jeda.
 * 2. Instant button spinner & dimension locking.
 * 3. Slim Neobrutalist top progress bar synchronization (topProgress).
 * 4. Automatic network request (fetch/XHR) lifecycle tracking.
 * 5. Global loader synchronization (Utils.showLoading & useLoadingStore).
 * 6. Multi-click spam prevention & ongoing process busy-locking.
 */

import { topProgress } from './topProgressService.js';

let lastActiveButton = null;
let lastActiveTime = 0;
let clickPendingTimer = null;
let pendingNetworkCount = 0;
const buttonTimers = new WeakMap();

/**
 * Explicitly set a button to loading or normal state
 * @param {HTMLElement} btn 
 * @param {boolean} isLoading 
 * @param {string|null} customText 
 */
export function setButtonLoading(btn, isLoading = true, customText = null) {
  if (!btn || !(btn instanceof Element)) return;

  if (isLoading) {
    if (btn.classList.contains('is-loading')) return;

    // Lock min-width and min-height to prevent button jumping/shrinking
    const rect = btn.getBoundingClientRect();
    if (rect.width > 0 && !btn.style.minWidth) {
      btn.style.minWidth = `${rect.width}px`;
      btn._nbResetMinWidth = true;
    }

    // Save previous state
    if (!btn._originalHtml) {
      btn._originalHtml = btn.innerHTML;
    }
    if (typeof btn._originalDisabled === 'undefined') {
      btn._originalDisabled = btn.disabled;
    }

    btn.classList.add('is-loading');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    // Handle icon: look for existing <i> or create one
    const icon = btn.querySelector('i');
    if (icon && !icon.classList.contains('fa-spinner') && !icon.classList.contains('fa-spin')) {
      icon.dataset.origClass = icon.className;
      icon.className = 'fas fa-spinner fa-spin nb-btn-spinner';
    } else if (!icon) {
      const spinner = document.createElement('i');
      spinner.className = 'fas fa-spinner fa-spin nb-btn-spinner';
      spinner.style.marginRight = '0.4rem';
      spinner.style.display = 'inline-block';
      btn.prepend(spinner);
    }

    if (customText) {
      const textSpan = btn.querySelector('.btn-text') || btn;
      if (textSpan !== btn) {
        textSpan.dataset.origText = textSpan.textContent;
        textSpan.textContent = customText;
      }
    }

    // Safety timeout: auto-release after 15s so buttons are never permanently stuck
    const timer = setTimeout(() => {
      setButtonLoading(btn, false);
    }, 15000);
    buttonTimers.set(btn, timer);
  } else {
    if (!btn.classList.contains('is-loading')) return;

    // Clear safety timer
    const timer = buttonTimers.get(btn);
    if (timer) {
      clearTimeout(timer);
      buttonTimers.delete(btn);
    }

    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');

    // Restore minWidth if locked
    if (btn._nbResetMinWidth) {
      btn.style.minWidth = '';
      delete btn._nbResetMinWidth;
    }

    // Restore original HTML if preserved
    if (btn._originalHtml) {
      btn.innerHTML = btn._originalHtml;
      delete btn._originalHtml;
    } else {
      const spinner = btn.querySelector('.nb-btn-spinner');
      if (spinner) spinner.remove();

      const icon = btn.querySelector('i');
      if (icon && icon.dataset.origClass) {
        icon.className = icon.dataset.origClass;
        delete icon.dataset.origClass;
      }
    }

    // Restore original disabled status
    if (typeof btn._originalDisabled !== 'undefined') {
      btn.disabled = !!btn._originalDisabled;
      delete btn._originalDisabled;
    } else {
      btn.disabled = false;
    }
  }
}

/**
 * Initialize universal button loading & process interceptor
 */
export function initGlobalButtonLoading() {
  if (typeof window === 'undefined' || window.__nbButtonLoadingInitialized) return;
  window.__nbButtonLoadingInitialized = true;

  // Expose global setter for manual/legacy invocation
  window.__setButtonLoading = setButtonLoading;
  if (!window.Utils) window.Utils = {};
  window.Utils.setButtonLoading = setButtonLoading;
  window.__topProgress = topProgress;

  // 1. Instant Tactile Pointer Feedback (0ms on pointerdown / touchstart)
  document.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest('button, .btn, input[type="submit"], [role="button"], .tab-btn, .sidebar-nav-item, .course-nav-item, .bottom-nav-item');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    btn.classList.add('nb-btn-pressed');
  }, { capture: true, passive: true });

  const clearPressedState = (e) => {
    const btn = e.target.closest('button, .btn, input[type="submit"], [role="button"], .tab-btn, .sidebar-nav-item, .course-nav-item, .bottom-nav-item');
    if (btn) btn.classList.remove('nb-btn-pressed');
    // Also clean up any stray pressed states
    document.querySelectorAll('.nb-btn-pressed').forEach(b => b.classList.remove('nb-btn-pressed'));
  };

  document.addEventListener('pointerup', clearPressedState, { capture: true, passive: true });
  document.addEventListener('pointercancel', clearPressedState, { capture: true, passive: true });

  // 2. Capture click listener to trigger instant (0ms) loading & prevent multi-click spam
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button, .btn, input[type="submit"], [role="button"], .tab-btn, .sidebar-nav-item, .course-nav-item');
    if (!btn) return;

    btn.classList.remove('nb-btn-pressed');

    // If button is currently in an active loading state: suppress event completely
    if (btn.classList.contains('is-loading') || btn.getAttribute('aria-busy') === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // If a global process is running (full screen loader) and user clicks another action button:
    if (typeof window.__isGlobalProcessRunning === 'function' && window.__isGlobalProcessRunning()) {
      const isDismissOrClose = btn.matches('.modal-close, #closePreviewBtn, [data-dismiss], .btn-secondary, [title*="Tutup"], [aria-label*="Tutup"]');
      if (!isDismissOrClose) {
        e.preventDefault();
        e.stopImmediatePropagation();
        btn.classList.add('nb-shake');
        setTimeout(() => btn.classList.remove('nb-shake'), 400);
        if (window.Utils && typeof window.Utils.showToast === 'function') {
          window.Utils.showToast('Proses sedang berjalan, mohon tunggu sebentar...', 'warning');
        }
        return;
      }
    }

    lastActiveButton = btn;
    lastActiveTime = Date.now();

    // Check if this button is an action button that triggers loading / navigation
    const isDismiss = btn.matches(
      '.modal-close, #closePreviewBtn, [data-dismiss], [title*="Tutup"], [aria-label*="Tutup"], .sheet-handle-wrap, .sidebar-toggle-btn, .open-meeting-modal-btn, .meeting-grid-card, .btn-restore-dock, .btn-minimize-meeting, .edit-meeting-btn, .add-material-btn, [data-modal-target], [data-tab], .tab-btn, .course-tab, .sidebar-nav-item, .course-nav-item'
    );
    const isAction = !isDismiss && (
      btn.matches('input[type="submit"], button[type="submit"], .btn-primary, .btn-action, .btn-save, .btn-submit, .btn-danger, [data-action]') ||
      (btn.tagName === 'BUTTON' && !btn.classList.contains('btn-secondary')) ||
      btn.classList.contains('btn')
    );

    if (isAction) {
      topProgress.start();

      // Clear any prior provisional timer
      if (clickPendingTimer) clearTimeout(clickPendingTimer);

      // Provisional release timer: if no network call or global loader is sustained within 450ms,
      // gracefully finish top progress (e.g. for pure client-side tab switches or validation stops)
      clickPendingTimer = setTimeout(() => {
        if (pendingNetworkCount <= 0 && (!window.__isGlobalProcessRunning || !window.__isGlobalProcessRunning())) {
          if (lastActiveButton) {
            setButtonLoading(lastActiveButton, false);
          }
          topProgress.finish();
        }
      }, 450);
    }
  }, true);

  // 3. Global Network Tracking via window.fetch
  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      // Exclude background streams, Firestore Listen channels, token refreshes, and version polling
      // from tracking user-initiated button actions or driving the loading spinners.
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      const isBackgroundStream = (
        url.includes('firestore.googleapis.com') ||
        url.includes('identitytoolkit.googleapis.com') ||
        url.includes('securetoken.googleapis.com') ||
        url.includes('version.json') ||
        url.includes('google-analytics.com') ||
        url.includes('doubleclick.net')
      );

      if (isBackgroundStream) {
        return originalFetch.apply(this, args);
      }

      pendingNetworkCount++;
      // Only start progress on first fetch in batch
      if (pendingNetworkCount === 1) {
        if (clickPendingTimer) {
          clearTimeout(clickPendingTimer);
          clickPendingTimer = null;
        }
        topProgress.start();
      }
      if (lastActiveButton && (Date.now() - lastActiveTime < 1500) && document.body.contains(lastActiveButton)) {
        // Only set button loading if it's an action button and not a dismiss/modal trigger
        const isExcluded = lastActiveButton.matches(
          '.modal-close, #closePreviewBtn, [data-dismiss], [title*="Tutup"], [aria-label*="Tutup"], .open-meeting-modal-btn, .meeting-grid-card, .btn-restore-dock, .btn-minimize-meeting'
        );
        if (!isExcluded) {
          setButtonLoading(lastActiveButton, true);
        }
      }

      try {
        const response = await originalFetch.apply(this, args);
        return response;
      } finally {
        pendingNetworkCount--;
        if (pendingNetworkCount <= 0) {
          pendingNetworkCount = 0;
          topProgress.finish(true);
          if (lastActiveButton) {
            setButtonLoading(lastActiveButton, false);
            lastActiveButton = null;
          }
        }
      }
    };
  }

  // 4. Connect to window.__triggerVueLoader
  const previousVueLoader = window.__triggerVueLoader;
  window.__triggerVueLoader = (show, message = 'Memproses...', force = false) => {
    if (show) {
      if (clickPendingTimer) {
        clearTimeout(clickPendingTimer);
        clickPendingTimer = null;
      }
      topProgress.start();
      // If an action button was recently clicked within 1.5s, also activate its loading state
      if (lastActiveButton && (Date.now() - lastActiveTime < 1500) && document.body.contains(lastActiveButton)) {
        const isExcluded = lastActiveButton.matches(
          '.modal-close, #closePreviewBtn, [data-dismiss], [title*="Tutup"], [aria-label*="Tutup"], .open-meeting-modal-btn, .meeting-grid-card, .btn-restore-dock, .btn-minimize-meeting'
        );
        if (!isExcluded) {
          setButtonLoading(lastActiveButton, true);
        }
      }
    } else {
      topProgress.finish(force);
      if (lastActiveButton) {
        setButtonLoading(lastActiveButton, false);
        lastActiveButton = null;
      }
      // Also release any stray loading buttons on page when process completes
      document.querySelectorAll('button.is-loading, .btn.is-loading').forEach(b => {
        setButtonLoading(b, false);
      });
    }

    if (typeof previousVueLoader === 'function') {
      previousVueLoader(show, message, force);
    }
  };
}

